import React, { useState } from "react";
import {
  SFStack,
  SFTypography,
  SFIcons,
  SFIconButton,
  SFUseStyled,
  SFAccordion,
  SFAccordionSummary,
  SFAccordionDetails,
} from "@sf-core";
import CourseItem from "./courseItem";
import { useTranslation } from "react-i18next";
import GroupInfoDialog from "./groupInfoDialog";
import { STATUS } from "../../constants/statuses";

const { styled } = SFUseStyled();

const StyledAccordion = styled(SFAccordion)(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, 0)",
  paddingTop: theme.spacing(1),
  marginTop: "0px !important",
}));

const SFAccordionSummaryStyled = styled(SFAccordionSummary)(({ theme }) => ({
  backgroundColor: "rgba(196, 196, 205, 0.1)",
  borderRadius: theme.spacing(1),
  flexDirection: "row-reverse",
  paddingLeft: theme.spacing(1),
  height: "40px !important",
  minHeight: "40px !important",
  "& .MuiAccordionSummary-content": {
    pointerEvents: "none",
  },
  "& .MuiButtonBase-root": {
    pointerEvents: "auto",
  },
}));

const SFAccordionDetailsStyled = styled(SFAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0),
  color: "white !important",
  "& .skill-wrapper": {
    padding: theme.spacing(2, 0),
  },
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  height: "100%",
  paddingLeft: theme.spacing(1),
}));

const CourseGroup = ({
  courses,
  descriptionLines,
  skillPathLevelId,
  onClick,
  defaultExpanded = true,
  isUpSkillingGroup = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [groupDialog, setGroupDialog] = useState({ open: false });
  let accordionTitle = t("coursesGrouping.refreshKnowledge.label");
  if (isUpSkillingGroup) accordionTitle = t("coursesGrouping.upSkilling.label");

  const handeOpenGroupDialog = () => {
    //Find the unique skills for all the group coursed
    const groupSkills = courses.reduce((accumulator, course) => {
      course.skills.forEach((skill) => {
        if (!accumulator.some((accSkill) => accSkill.id === skill.id)) {
          accumulator.push(skill);
        }
      });
      return accumulator;
    }, []);

    setGroupDialog({
      open: true,
      title: isUpSkillingGroup
        ? t("coursesGrouping.upSkilling.popupTitle")
        : t("coursesGrouping.refreshKnowledge.popupTitle"),
      description: isUpSkillingGroup
        ? t("coursesGrouping.upSkilling.popupDescription")
        : t("coursesGrouping.refreshKnowledge.popupDescription"),
      skills: groupSkills,
      closeButton: t("coursesGrouping.popupCloseBtn"),
      label: t("coursesGrouping.popupLabel"),
      secondaryButton: courseToStart()
    });
  };

  // Find the course to start or resume from the group info popup button if there is one
  const courseToStart = () => {
    const sortedCourses = [...courses].sort((a, b) => a.rank - b.rank);
    const notStarted = sortedCourses.filter(
      (course) => course.status === STATUS.NOT_STARTED
    );
    const inProgress = sortedCourses
      .filter((course) => course.status === STATUS.IN_PROGRESS)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    const completed = sortedCourses.filter(
      (course) => course.status === STATUS.COMPLETED
    );

    // If all courses completed display no Button
    if (completed.length === courses.length) {
      return null;
    }
    // If no courses started display Start Button which navigates to first course
    if (notStarted.length === courses.length) {
      return { courseId: notStarted[0].id, label: t("coursesGrouping.popupStartBtn") };
    }
    // If there is at least 1 In Progress course display Resume Button which navigates to last viewed course
    if (inProgress.length > 0) {
      return { courseId: inProgress[0].id, label: t("coursesGrouping.popupResumeBtn") };
    }
    // Else display Resume Button which navigates to first Not Started course
    if (completed.length > 0 && notStarted.length > 0) {
      return { courseId: notStarted[0].id, label: t("coursesGrouping.popupResumeBtn") };
    }
  };

  return (
    <>
      <GroupInfoDialog
        groupDialog={groupDialog}
        setGroupDialog={setGroupDialog}
        onClose={() => setGroupDialog({ open: false })}
        skillPathId={skillPathLevelId}
      />
      {courses?.length > 0 && (
        <StyledAccordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
        >
          <SFAccordionSummaryStyled>
            <StyledStack direction="row" alignItems="center">
              <SFTypography variant="body1">{accordionTitle}</SFTypography>
              <SFIconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handeOpenGroupDialog();
                }}
                variant="transparent"
              >
                <SFIcons.SFInfoIcon size="small" />
              </SFIconButton>
            </StyledStack>
          </SFAccordionSummaryStyled>
          <SFAccordionDetailsStyled>
            <div className="details-wrapper">
              {courses?.map((item, index) => (
                <div className="skill-wrapper" key={index}>
                  <CourseItem
                    key={index}
                    course={item}
                    skillPathLevelId={skillPathLevelId}
                    onClick={onClick}
                    descriptionLines={descriptionLines}
                  />
                </div>
              ))}
            </div>
          </SFAccordionDetailsStyled>
        </StyledAccordion>
      )}
    </>
  );
};

export default CourseGroup;
